<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import type { StuckCartsResponse, StuckCart } from '@/types/fulfillment'
import { DashboardReasonConfirmDialog } from '@/components/dashboard/common'

defineOptions({ name: 'StuckCartsList' })

const emit = defineEmits<{
  refresh: []
}>()

const { t } = useI18n()
const notifyStore = useNotifyStore()

const loading = ref(true)
const stuckCarts = ref<StuckCart[]>([])
const threshold = ref('')
const expandedCarts = ref<Set<string>>(new Set())

const refundDialog = ref({
  show: false,
  cart: null as StuckCart | null,
  loading: false
})

// Fetch stuck carts
async function fetchStuckCarts() {
  loading.value = true

  try {
    const res = await useAxios<null, StuckCartsResponse>(
      'GET',
      '/admin/fulfillment/stuck',
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      stuckCarts.value = res.data.stuck_carts ?? []
      threshold.value = res.data.threshold ?? ''
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'StuckCartsList' })
  } finally {
    loading.value = false
  }
}

function toggleExpand(cartId: string) {
  if (expandedCarts.value.has(cartId)) {
    expandedCarts.value.delete(cartId)
  } else {
    expandedCarts.value.add(cartId)
  }
}

function isExpanded(cartId: string): boolean {
  return expandedCarts.value.has(cartId)
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'completed': return 'status-completed'
    case 'failed': return 'status-failed'
    case 'in_progress': return 'status-progress'
    case 'pending': return 'status-pending'
    case 'compensated': return 'status-compensated'
    default: return ''
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'completed': return 'Abgeschlossen'
    case 'failed': return 'Fehlgeschlagen'
    case 'in_progress': return 'In Bearbeitung'
    case 'pending': return 'Ausstehend'
    case 'compensated': return 'Kompensiert'
    default: return status
  }
}

function getItemTypeLabel(itemType: string): string {
  switch (itemType) {
    case 'domain_registration': return 'Domain-Registrierung'
    case 'domain_transfer': return 'Domain-Transfer'
    case 'internal_transfer': return 'Interner Transfer'
    case 'hosting': return 'Hosting'
    case 'k8s': return 'Kubernetes'
    default: return itemType
  }
}

function truncateId(id: string): string {
  if (id.length <= 12) return id
  return `${id.substring(0, 6)}...${id.substring(id.length - 4)}`
}

function handleRefresh() {
  fetchStuckCarts()
  emit('refresh')
}

function openRefundDialog(cart: StuckCart) {
  refundDialog.value.cart = cart
  refundDialog.value.show = true
}

async function confirmRefund(reason: string) {
  const cart = refundDialog.value.cart
  if (!cart) return

  refundDialog.value.loading = true

  try {
    const res = await useAxios<{ reason: string }, { message?: string }>(
      'POST',
      `/admin/cart/${cart.id}/refund`,
      true,
      { reason }
    )

    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.fulfillment.refund.success'),
        message: cart.account_email
      })
      refundDialog.value.show = false
      handleRefresh()
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'StuckCartsList:refund' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(err as Error)
    })
  } finally {
    refundDialog.value.loading = false
  }
}

onMounted(() => {
  fetchStuckCarts()
})
</script>

<template>
  <div class="stuck-carts-list">
    <!-- Header -->
    <div class="list-header">
      <div class="header-left">
        <div class="header-icon">
          <v-icon icon="mdi-cart-off" size="20" />
        </div>
        <div>
          <h2 class="list-title">Hängende Warenkörbe</h2>
          <p class="list-subtitle">
            Carts, die länger als {{ threshold }} im Fulfillment hängen
          </p>
        </div>
      </div>
      <button class="refresh-btn" @click="handleRefresh">
        <v-icon icon="mdi-refresh" size="18" />
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular color="orange" indeterminate size="40" width="3" />
      <span class="loading-text">Lade hängende Carts...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="stuckCarts.length === 0" class="empty-state">
      <div class="empty-icon">
        <v-icon icon="mdi-check-circle" size="48" />
      </div>
      <span class="empty-title">Keine hängenden Carts</span>
      <span class="empty-subtitle">Alle Fulfillment-Prozesse laufen normal</span>
    </div>

    <!-- Stuck Carts Table -->
    <div v-else class="carts-table">
      <div class="table-head">
        <div class="head-cell cell-cart">Cart ID</div>
        <div class="head-cell cell-account">Kunde</div>
        <div class="head-cell cell-payment">Payment ID</div>
        <div class="head-cell cell-duration">Dauer</div>
        <div class="head-cell cell-items">Items</div>
        <div class="head-cell cell-expand"></div>
      </div>

      <div
        v-for="cart in stuckCarts"
        :key="cart.id"
        class="cart-row-wrapper"
      >
        <!-- Main Row -->
        <div class="table-row" :class="{ expanded: isExpanded(cart.id) }">
          <div class="row-cell cell-cart">
            <span class="cart-id">{{ truncateId(cart.id) }}</span>
          </div>
          <div class="row-cell cell-account">
            <span class="account-email">{{ cart.account_email }}</span>
          </div>
          <div class="row-cell cell-payment">
            <span class="payment-id">{{ truncateId(cart.payment_id) }}</span>
          </div>
          <div class="row-cell cell-duration">
            <span class="duration-badge">{{ cart.stuck_duration }}</span>
          </div>
          <div class="row-cell cell-items">
            <span class="items-count">{{ cart.domain_items?.length ?? 0 }} Domain(s)</span>
            <span class="tasks-count">{{ cart.tasks?.length ?? 0 }} Task(s)</span>
          </div>
          <div class="row-cell cell-expand">
            <button class="expand-btn" @click="toggleExpand(cart.id)">
              <v-icon
                :icon="isExpanded(cart.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                size="20"
              />
            </button>
          </div>
        </div>

        <!-- Expanded Details -->
        <div v-if="isExpanded(cart.id)" class="expanded-content">
          <!-- Domain Items -->
          <div v-if="cart.domain_items?.length" class="detail-section">
            <h4 class="section-title">
              <v-icon icon="mdi-web" size="16" />
              Domain Items
            </h4>
            <div class="detail-table">
              <div
                v-for="item in cart.domain_items"
                :key="item.id"
                class="detail-row"
              >
                <span class="domain-name">{{ item.domain_name }}</span>
                <span class="status-badge" :class="getStatusClass(item.fulfillment_status)">
                  {{ getStatusLabel(item.fulfillment_status) }}
                </span>
                <span v-if="item.fulfillment_error" class="error-text">
                  {{ item.fulfillment_error }}
                </span>
              </div>
            </div>
          </div>

          <!-- Tasks -->
          <div v-if="cart.tasks?.length" class="detail-section">
            <h4 class="section-title">
              <v-icon icon="mdi-format-list-checks" size="16" />
              Fulfillment Tasks
            </h4>
            <div class="detail-table">
              <div
                v-for="task in cart.tasks"
                :key="task.id"
                class="detail-row task-row"
              >
                <span class="task-type">{{ getItemTypeLabel(task.item_type) }}</span>
                <span class="status-badge" :class="getStatusClass(task.status)">
                  {{ getStatusLabel(task.status) }}
                </span>
                <span class="task-step">Step {{ task.step }}</span>
                <span class="task-attempts">{{ task.attempts }} Versuche</span>
                <span v-if="task.error" class="error-text">
                  {{ task.error }}
                </span>
              </div>
            </div>
          </div>

          <!-- Cart Actions -->
          <div class="cart-actions">
            <span v-if="cart.status === 'refunded'" class="refunded-badge">
              <v-icon icon="mdi-credit-card-check" size="14" />
              {{ t('dashboard.admin.fulfillment.refund.refunded') }}
            </span>
            <button v-else class="refund-btn" @click="openRefundDialog(cart)">
              <v-icon icon="mdi-credit-card-refund" size="16" />
              <span>{{ t('dashboard.admin.fulfillment.refund.button') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Refund Confirmation Dialog -->
    <DashboardReasonConfirmDialog
      v-model="refundDialog.show"
      :title="t('dashboard.admin.fulfillment.refund.dialogTitle')"
      :subtitle="refundDialog.cart?.account_email"
      :description="t('dashboard.admin.fulfillment.refund.dialogMessage')"
      :warning="t('dashboard.admin.fulfillment.refund.warning')"
      icon="mdi-credit-card-refund"
      variant="danger"
      :confirm-text="t('dashboard.admin.fulfillment.refund.confirm')"
      confirm-icon="mdi-credit-card-refund"
      :loading="refundDialog.loading"
      :reason-label="t('dashboard.admin.fulfillment.refund.reasonLabel')"
      :reason-placeholder="t('dashboard.admin.fulfillment.refund.reasonPlaceholder')"
      @confirm="confirmRefund"
      @cancel="refundDialog.cart = null"
    />
  </div>
</template>

<style lang="scss" scoped>
.stuck-carts-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(239, 83, 80, 0.1);
  border: 1px solid rgba(239, 83, 80, 0.2);
  border-radius: 10px;

  .v-icon {
    color: #ef5350;
  }
}

.list-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.list-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.refresh-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
    color: rgba(255, 255, 255, 0.9);
  }
}

/* Loading & Empty States */
.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 200px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.loading-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 16px;

  .v-icon {
    color: #4caf50;
  }
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.empty-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Table */
.carts-table {
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.table-head {
  display: grid;
  grid-template-columns: 120px 1fr 150px 100px 150px 50px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.head-cell {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cart-row-wrapper {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
}

.table-row {
  display: grid;
  grid-template-columns: 120px 1fr 150px 100px 150px 50px;
  padding: 14px 16px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &.expanded {
    background: rgba(239, 83, 80, 0.05);
  }
}

.row-cell {
  display: flex;
  align-items: center;
}

.cart-id,
.payment-id {
  font-family: monospace;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.7);
}

.account-email {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
}

.duration-badge {
  padding: 4px 10px;
  background: rgba(239, 83, 80, 0.15);
  border: 1px solid rgba(239, 83, 80, 0.25);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #ef5350;
}

.cell-items {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.items-count,
.tasks-count {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
}

.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
  }
}

/* Expanded Content */
.expanded-content {
  padding: 16px;
  background: rgba(15, 23, 48, 0.3);
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.detail-section {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.8);

  .v-icon {
    color: rgba(200, 218, 255, 0.5);
  }
}

.detail-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 14px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 8px;
}

.domain-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.task-type {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.task-step,
.task-attempts {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.error-text {
  font-size: 0.8rem;
  color: #ef5350;
  margin-left: auto;
}

/* Cart Actions */
.cart-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.refund-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(239, 83, 80, 0.12);
  border: 1px solid rgba(239, 83, 80, 0.3);
  border-radius: 8px;
  color: #ef5350;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 83, 80, 0.22);
    border-color: rgba(239, 83, 80, 0.45);
  }
}

.refunded-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(156, 39, 176, 0.12);
  border: 1px solid rgba(156, 39, 176, 0.25);
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #ce93d8;
}

/* Status Badges */
.status-badge {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;

  &.status-completed {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
  }

  &.status-failed {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }

  &.status-progress {
    background: rgba(0, 176, 255, 0.15);
    color: #00b0ff;
  }

  &.status-pending {
    background: rgba(255, 193, 7, 0.15);
    color: #ffc107;
  }

  &.status-compensated {
    background: rgba(156, 39, 176, 0.15);
    color: #9c27b0;
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .list-title,
  .empty-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .list-subtitle,
  .empty-subtitle,
  .loading-text {
    color: rgba(15, 23, 42, 0.6);
  }

  .carts-table,
  .loading-container,
  .empty-state {
    background: rgba(255, 255, 255, 0.6);
  }

  .account-email,
  .domain-name,
  .task-type {
    color: rgba(15, 23, 42, 0.9);
  }

  .cart-id,
  .payment-id,
  .task-step,
  .task-attempts {
    color: rgba(15, 23, 42, 0.6);
  }
}

/* Responsive */
@media (max-width: 900px) {
  .table-head,
  .table-row {
    grid-template-columns: 1fr 1fr 80px 50px;
  }

  .cell-payment,
  .cell-items {
    display: none;
  }
}

@media (max-width: 600px) {
  .table-head {
    display: none;
  }

  .table-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px;
  }

  .row-cell {
    width: 100%;
    justify-content: space-between;
  }

  .cell-expand {
    justify-content: flex-end;
  }
}
</style>
